import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  ErrorBanner,
  PageHeader,
  toast,
} from '@saas-kit/ui';
import type { BulkAction, PaginationState } from '@saas-kit/ui/DataTable';

import {
  createUser,
  listUsers,
  reactivateUser,
  suspendUser,
  type AdminUser,
  type CreateUserPayload,
} from './api';
import { buildUserBulkActions } from './bulkActions';
import { UserCreateForm } from './UserCreateForm';
import { UsersTable } from './UsersTable';

const DEFAULT_PAGE_SIZE = 25;

type ActionKey = 'suspend' | 'reactivate';

interface PendingAction {
  userId: string;
  action: ActionKey;
}

export function UsersPage(): JSX.Element {
  const { t } = useTranslation('adminUsers');
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [emailFilter, setEmailFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const trimmedFilter = emailFilter.trim();

  const usersQuery = useQuery({
    queryKey: ['admin-users', { offset, limit: pageSize, email: trimmedFilter }],
    queryFn: () =>
      listUsers({
        offset,
        limit: pageSize,
        email: trimmedFilter || null,
      }),
    placeholderData: (prev) => prev,
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  }

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: invalidate,
  });

  function buildStatusMutation(
    action: ActionKey,
    fn: (id: string) => Promise<AdminUser>,
    toastKey: string,
  ) {
    return {
      mutationFn: async (user: AdminUser) => {
        setPending({ userId: user.id, action });
        try {
          return await fn(user.id);
        } finally {
          setPending(null);
        }
      },
      onSuccess: async () => {
        await invalidate();
        toast.success(t(toastKey));
      },
      onError: (err: unknown) => toast.error(parseApiError(err).message),
    };
  }

  const suspendMutation = useMutation(
    buildStatusMutation('suspend', suspendUser, 'toasts.suspended'),
  );
  const reactivateMutation = useMutation(
    buildStatusMutation('reactivate', reactivateUser, 'toasts.reactivated'),
  );

  const bulkActions = useMemo<BulkAction<AdminUser>[]>(
    () => buildUserBulkActions({ t, invalidate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  async function handleCreate(payload: CreateUserPayload): Promise<void> {
    await createMutation.mutateAsync(payload);
  }

  function handleSearchChange(next: string): void {
    setEmailFilter(next);
    setOffset(0);
  }

  const users = usersQuery.data?.items ?? [];
  const page = usersQuery.data?.page;
  const total = page?.total ?? 0;
  const hasNext = page ? page.offset + page.limit < page.total : false;
  const hasPrev = offset > 0;

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext,
    hasPrev,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const showEmpty =
    !usersQuery.isLoading && total === 0 && !usersQuery.isError && !trimmedFilter;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <Button onClick={() => setCreateOpen(true)}>{t('actions.create')}</Button>
        }
      />

      {usersQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}

      {showEmpty ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState title={t('empty.title')} description={t('empty.body')} />
          </CardContent>
        </Card>
      ) : (
        <UsersTable
          users={users}
          pendingUserId={pending?.userId ?? null}
          pendingAction={pending?.action ?? null}
          pagination={pagination}
          isLoading={usersQuery.isLoading}
          error={usersQuery.isError ? t('errors.load_failed') : null}
          bulkActions={bulkActions}
          onSuspend={(u) => suspendMutation.mutate(u)}
          onReactivate={(u) => reactivateMutation.mutate(u)}
          search={emailFilter}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t('filters.email_placeholder')}
        />
      )}

      <UserCreateForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        isPending={createMutation.isPending}
        onSubmit={handleCreate}
      />
    </div>
  );
}
